import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        black: "bg-gray-900 border border-gray-900 text-white shadow-xs hover:bg-transparent hover:text-gray-900 dark:bg-foreground dark:border-foreground dark:text-background",
        action: "bg-primary/10 border border-primary text-blue-800 shadow-xs hover:bg-primary/30 dark:text-blue-200",
        actionForm: "bg-primary/10 border border-primary text-black shadow-xs hover:bg-primary/30 w-full",
        outline: "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary/10 border border-secondary/30 text-black shadow-xs hover:bg-secondary/30",
        ghost: "bg-ghost/20 shadow-xs hover:bg-ghost/30",
        actionGhost:
          "bg-primary-gray/10 border border-primary-gray text-black shadow-xs hover:bg-primary/10 hover:border-primary hover:text-primary",
        destructive:
          "bg-destructive/10 border border-destructive text-destructive-dark shadow-xs hover:bg-destructive/30",
        destructiveGhost:
          "bg-primary-gray/10 border border-primary-gray text-black shadow-xs hover:bg-destructive/10 hover:border-destructive hover:text-destructive",
        warning: "bg-warning/10 border border-warning text-orange-700 shadow-xs hover:bg-warning/20 dark:text-orange-200",
        primaryGhost:
          "bg-primary-gray/10 border border-primary-gray text-black shadow-xs hover:bg-primary/10 hover:border-primary hover:text-primary",
        success: "bg-success/10 border border-success text-success-dark shadow-xs hover:bg-success/30",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "black",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button">
  & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({
        variant,
        size,
        className,
      }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
